import React from 'react';
import { CalculationResult } from '../types/calculator';
import { Table, Download } from 'lucide-react';

interface BreakdownTableProps {
  result: CalculationResult;
}

export const BreakdownTable: React.FC<BreakdownTableProps> = ({ result }) => {
  const formatGBP = (val: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  const rows = [
    { name: 'Yearly', data: result.breakdowns.yearly },
    { name: 'Monthly', data: result.breakdowns.monthly },
    { name: '4-Weekly', data: result.breakdowns.fourWeekly },
    { name: 'Fortnightly', data: result.breakdowns.fortnightly },
    { name: 'Weekly', data: result.breakdowns.weekly },
    { name: 'Daily (5d/wk)', data: result.breakdowns.daily },
    { name: 'Hourly (37.5h/wk)', data: result.breakdowns.hourly },
  ];

  const handleExportCSV = () => {
    const headers = ['Period', 'Gross Pay', 'Income Tax', 'National Insurance', 'Pension', 'Student Loan', 'Total Deductions', 'Net Pay'];
    const csvRows = rows.map(r => [
      r.name,
      r.data.gross.toFixed(2),
      r.data.incomeTax.toFixed(2),
      r.data.nationalInsurance.toFixed(2),
      r.data.pension.toFixed(2),
      r.data.studentLoan.toFixed(2),
      r.data.totalDeductions.toFixed(2),
      r.data.netPay.toFixed(2),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...csvRows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `UK_Take_Home_Pay_Breakdown_${result.inputs.grossIncome}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5 sm:p-6 space-y-4 shadow-2xl text-zinc-100">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-zinc-800 pb-4">
        <div>
          <h2 className="text-lg font-extrabold text-white flex items-center gap-2">
            <Table className="w-5 h-5 text-emerald-400" />
            Complete Salary Breakdown Matrix
          </h2>
          <p className="text-xs text-zinc-400 mt-0.5">
            Compare deductions across all time periods (Annual down to Hourly)
          </p>
        </div>

        <button
          onClick={handleExportCSV}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl bg-zinc-950 hover:bg-zinc-800 text-zinc-200 transition-colors self-start sm:self-auto border border-zinc-800"
        >
          <Download className="w-3.5 h-3.5 text-emerald-400" />
          <span>Export CSV</span>
        </button>
      </div>

      <div className="overflow-x-auto -mx-5 sm:mx-0">
        <table className="w-full text-left text-xs font-mono border-collapse min-w-[640px]">
          <thead>
            <tr className="bg-zinc-950 text-zinc-400 font-sans uppercase font-bold border-y border-zinc-800 text-[11px] tracking-wider">
              <th className="py-3 px-4">Period</th>
              <th className="py-3 px-4 text-white">Gross Pay</th>
              <th className="py-3 px-4 text-rose-400">Income Tax</th>
              <th className="py-3 px-4 text-amber-400">NI</th>
              <th className="py-3 px-4 text-purple-400">Pension</th>
              <th className="py-3 px-4 text-sky-400">Student Loan</th>
              <th className="py-3 px-4 text-zinc-300">Total Deduct</th>
              <th className="py-3 px-4 bg-emerald-950/60 text-emerald-400 font-extrabold border-l border-zinc-800">Net Take Home</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/80">
            {rows.map((row, idx) => (
              <tr
                key={row.name}
                className={`hover:bg-zinc-950/60 transition-colors ${
                  idx === 1 ? 'bg-emerald-950/20 font-semibold' : ''
                }`}
              >
                <td className="py-3 px-4 font-sans font-bold text-white">{row.name}</td>
                <td className="py-3 px-4 font-bold text-zinc-200">{formatGBP(row.data.gross)}</td>
                <td className="py-3 px-4 text-rose-400 font-medium">-{formatGBP(row.data.incomeTax)}</td>
                <td className="py-3 px-4 text-amber-400 font-medium">-{formatGBP(row.data.nationalInsurance)}</td>
                <td className="py-3 px-4 text-purple-400 font-medium">
                  {row.data.pension > 0 ? `-${formatGBP(row.data.pension)}` : '£0.00'}
                </td>
                <td className="py-3 px-4 text-sky-400 font-medium">
                  {row.data.studentLoan > 0 ? `-${formatGBP(row.data.studentLoan)}` : '£0.00'}
                </td>
                <td className="py-3 px-4 text-zinc-300 font-semibold">-{formatGBP(row.data.totalDeductions)}</td>
                <td className="py-3 px-4 bg-emerald-950/40 font-extrabold text-emerald-400 text-sm border-l border-zinc-800">
                  {formatGBP(row.data.netPay)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

